'use client';

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { dedupedGet } from '../lib/requestDedupe';

const PACKS_API = '/api/packs/v3';

// force bypasses coalescing so an explicit refetch always hits the network.
async function fetchPacksData({ force = false } = {}) {
  const response = await (force ? axios.get(PACKS_API) : dedupedGet(PACKS_API));
  return {
    packs: response?.data?.data?.purchasedPacks || [],
    freePacksRes: response?.data?.data?.freePacks || [],
  };
}

export function usePackStorage() {
  const [purchasedPacks, setPurchasedPacks] = useState([]);
  const [freePacks, setFreePacks] = useState([]);
  const [purchasedFreePacksCollective, setPurchasedFreePacksCollective] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const updateURLIfPackMismatch = (packId) => {
    const currentPath = location.pathname;

    if (currentPath.includes('/v3/') && currentPath.includes('/tests')) {
      const pathParts = currentPath.split('/');
      const v3Index = pathParts.indexOf('v3');

      if (v3Index !== -1 && pathParts[v3Index + 1]) {
        const currentPackId = pathParts[v3Index + 1];
        if (currentPackId !== packId) {
          pathParts[v3Index + 1] = packId;
          const newPath = pathParts.join('/');
          navigate(newPath, { replace: true });
        }
      }
    } else if (currentPath.includes('/v3/') && currentPath.includes('/resources')) {
      const pathParts = currentPath.split('/');
      const v3Index = pathParts.indexOf('v3');

      if (v3Index !== -1 && pathParts[v3Index + 1]) {
        const currentPackId = pathParts[v3Index + 1];
        if (currentPackId !== packId) {
          pathParts[v3Index + 1] = packId;
          const newPath = pathParts.join('/');
          navigate(newPath, { replace: true });
        }
      }
    } else if (currentPath.includes('/v2/') && currentPath.includes('/analysis')) {
      const pathParts = currentPath.split('/');
      const v2Index = pathParts.indexOf('v2');

      if (v2Index !== -1 && pathParts[v2Index + 1]) {
        const currentPackId = pathParts[v2Index + 1];
        if (currentPackId !== packId) {
          pathParts[v2Index + 1] = packId;
          const newPath = pathParts.join('/');
          navigate(newPath, { replace: true });
        }
      }
    }
  };

  const loadFromStorage = () => {
    try {
      const storedPacks = localStorage.getItem('purchasedPacks');
      const storedSelectedPack = localStorage.getItem('selectedPack');

      if (storedPacks) {
        const packs = JSON.parse(storedPacks);
        setPurchasedPacks(packs);
      }

      if (storedSelectedPack) {
        const selected = JSON.parse(storedSelectedPack);

        updateURLIfPackMismatch(selected._id);
        setSelectedPack(selected);
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  };

  const saveToStorage = (packs, selected, isFree = false) => {
    try {
      localStorage.setItem(`${isFree ? 'freePacks' : 'purchasedPacks'}`, JSON.stringify(packs));
      if (selected) {
        localStorage.setItem('selectedPack', JSON.stringify(selected));
        localStorage.setItem('selectedPackId', selected._id);
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const handlePackChange = (packId) => {
    if (packId === 'view-other-tests') {
      window.open('https://www.quizrr.in/#packs', '_blank');
      return;
    }

    const selectedPackData = purchasedFreePacksCollective.find((pack) => pack._id === packId);
    if (selectedPackData) {
      setSelectedPack(selectedPackData);
      saveToStorage(purchasedPacks, selectedPackData);

      window.dispatchEvent(
        new CustomEvent('packChanged', {
          detail: { selectedPack: selectedPackData, purchasedFreePacksCollective },
        }),
      );

      const currentPath = location.pathname;

      if (currentPath.includes('/v3/') && currentPath.includes('/tests')) {
        const pathParts = currentPath.split('/');
        const v3Index = pathParts.indexOf('v3');

        if (v3Index !== -1 && pathParts[v3Index + 1]) {
          const currentPackId = pathParts[v3Index + 1];
          if (currentPackId !== packId) {
            pathParts[v3Index + 1] = packId;
            const newPath = pathParts.join('/');

            navigate(newPath, { replace: true });
          }
        }
      } else if (currentPath.includes('/v3/') && currentPath.includes('/resources')) {
        const pathParts = currentPath.split('/');
        const v3Index = pathParts.indexOf('v3');

        if (v3Index !== -1 && pathParts[v3Index + 1]) {
          const currentPackId = pathParts[v3Index + 1];
          if (currentPackId !== packId) {
            pathParts[v3Index + 1] = packId;
            const newPath = pathParts.join('/');
            navigate(newPath, { replace: true });
          }
        }
      } else if (currentPath.includes('/v2/') && currentPath.includes('/analysis')) {
        const pathParts = currentPath.split('/');
        const v2Index = pathParts.indexOf('v2');

        if (v2Index !== -1 && pathParts[v2Index + 1]) {
          const currentPackId = pathParts[v2Index + 1];

          if (currentPackId !== packId) {
            pathParts[v2Index + 1] = packId;
            const newPath = pathParts.join('/');

            navigate(newPath, { replace: true });
          }
        }
      }
    }
  };

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedPack' || e.key === 'purchasedPacks') {
        loadFromStorage();
      }
    };

    const handlePackChangeEvent = (e) => {
      const { selectedPack: newSelectedPack, purchasedFreePacksCollective: newPurchasedPacks } = e.detail;
      setSelectedPack(newSelectedPack);
      setPurchasedPacks(newPurchasedPacks);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('packChanged', handlePackChangeEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('packChanged', handlePackChangeEvent);
    };
  }, []);

  const refetchPacks = async (preferredPackId) => {
    const { packs, freePacksRes } = await fetchPacksData({ force: true });
    const collective = [...packs, ...freePacksRes];

    setPurchasedPacks(packs);
    setFreePacks(freePacksRes);
    setPurchasedFreePacksCollective(collective);

    const savedPackId = localStorage.getItem('selectedPackId');
    const preferred = preferredPackId && collective.find((pack) => pack._id === preferredPackId);

    if (packs.length > 0) {
      let packToSelect = packs[0];
      if (preferred) {
        packToSelect = preferred;
      } else if (savedPackId) {
        const savedPack = packs.find((pack) => pack._id === savedPackId);
        if (savedPack) {
          packToSelect = savedPack;
        }
      }
      setSelectedPack(packToSelect);
      saveToStorage(packs, packToSelect);
      updateURLIfPackMismatch(packToSelect._id);
    } else if (freePacksRes.length > 0) {
      const packToSelect = preferred && freePacksRes.some((p) => p._id === preferred._id) ? preferred : freePacksRes[0];
      setSelectedPack(packToSelect);
      saveToStorage(freePacksRes, packToSelect, true);
      updateURLIfPackMismatch(packToSelect._id);
    } else {
      setSelectedPack(null);
    }
  };

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        setIsLoading(true);
        loadFromStorage();

        const { packs, freePacksRes } = await fetchPacksData();
        setPurchasedPacks(packs);
        setFreePacks(freePacksRes);
        setPurchasedFreePacksCollective([...packs, ...freePacksRes]);

        if (packs.length > 0) {
          const savedPackId = localStorage.getItem('selectedPackId');
          let packToSelect = packs[0];

          if (savedPackId) {
            const savedPack = packs.find((pack) => pack._id === savedPackId);
            if (savedPack) {
              packToSelect = savedPack;
            }
          }

          setSelectedPack(packToSelect);
          saveToStorage(packs, packToSelect);
        } else if (freePacksRes.length > 0) {
          setSelectedPack(freePacksRes[0]);
          saveToStorage(freePacksRes, freePacksRes[0], true);
        }
      } catch (error) {
        console.error('Error fetching packs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPacks();
  }, []);

  return {
    purchasedPacks,
    freePacks,
    purchasedFreePacksCollective,
    selectedPack,
    isLoading,
    handlePackChange,
    updateURLIfPackMismatch,
    refetchPacks,
  };
}
